import { GoogleLogin } from '@react-oauth/google';
import { Form, Image, Skeleton } from 'antd';
import Cookies from 'js-cookie';
import { useEffect, useMemo, useState } from 'react';
import { useMediaQuery } from 'react-responsive';
import { useTheme } from 'styled-components';

import { DrawerModal } from '@desygner/ui-common-components-next';

import { CONSTANTS } from './AuthModal.data';
import { PRESETS } from './AuthModal.presets';
import {
  StyledAuthFormWrapper,
  StyledAuthModalWrapper,
  StyledGoogleOneTapWrapper,
  StyledImageWrapper,
  StyledLogoSpacer,
  getStyleAuthImage,
  styleMainForm,
} from './AuthModal.styles';
import { AuthModalPropsT, LoginDtoT } from './AuthModal.types';
import { getServerErrorMessage, getWindowOrigin, isTokenExpired } from './AuthModal.utils';
import { ThemedFluerLogo } from './components/ThemedFluerLogo/ThemedFluerLogo';
import { useAuthContext } from './context/AuthContext';
import { AuthFlow } from './views/AuthFlow';

export const AuthModalView = ({
  useGoogleTapOnSign = true,
  onLogin: externalOnLogin,
  isAuthCloseDisabled,
  initView,
  onClose: externalOnClose,
  isOpened,
  labels,
  isDesygnerUser,
}: AuthModalPropsT) => {
  const [isImageLoading, setIsImageLoading] = useState(true);

  const theme = useTheme();
  const isDesktop = useMediaQuery({
    minWidth: theme.Screen.Size.Medium,
  });
  const [isGoogleTapOnSignVisible, setIsGoogleTapOnSignVisible] = useState(false);

  const {
    isAuthCloseTemporarilyDisabled,
    isLightTheme,
    setIsAuthCloseTemporarilyDisabled,
    setIsLoadingView,
    apiLogin,
    onLogin,
    onClose,
    setServerError,
    invitationToken,
    prefilledEmail,
  } = useAuthContext();

  const [form] = Form.useForm();

  const authImgSrcComputed = useMemo(() => {
    return isLightTheme ? CONSTANTS.lightThemeImageSrc : CONSTANTS.darkThemeImageSrc;
  }, [isLightTheme]);

  const isAuthCloseDisabledComputed = useMemo(() => {
    return isAuthCloseDisabled || isAuthCloseTemporarilyDisabled;
  }, [isAuthCloseTemporarilyDisabled, isAuthCloseDisabled]);

  const onHandleGoogleTapOnSignIn = async (token: string) => {
    const payload: LoginDtoT = {
      auth_token: token,
      auth_type: 'google',
      redirect_url: getWindowOrigin(),
      invitation_token: invitationToken,
    };

    setIsAuthCloseTemporarilyDisabled?.(true);
    setIsLoadingView?.(true);

    await apiLogin?.(payload)
      .then((res) => {
        if (!res.data.token) {
          const tokenError = 'User token is ' + res?.data?.token;

          console.error(tokenError);
          setServerError?.(tokenError);
          return;
        }

        onLogin?.(res.data.token);
        onClose?.();
      })
      .catch((err) => {
        setServerError?.(getServerErrorMessage(err) ?? 'Google login error');
      })
      .finally(() => {
        // Delay to prevent flickering
        setTimeout(() => {
          setIsLoadingView?.(false);
          setIsAuthCloseTemporarilyDisabled?.(false);
        }, 750);
      });
  };

  const onIsLoggedIn = async () => {
    const token = Cookies.get('user_token');

    if (token) {
      setIsGoogleTapOnSignVisible(isTokenExpired(token));
    } else {
      setIsGoogleTapOnSignVisible(true);
    }
  };

  useEffect(() => {
    if (!useGoogleTapOnSign) return;

    onIsLoggedIn();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (prefilledEmail) {
      form.setFieldValue('email', prefilledEmail);
    }
  }, [prefilledEmail]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <>
      <DrawerModal
        onClose={externalOnClose}
        isOpened={isOpened}
        width={isDesktop ? PRESETS.modalMaxWidth : '100%'}
        type={isAuthCloseDisabledComputed ? 'default' : 'floating'}
        isClosingDisabled={isAuthCloseDisabledComputed}
        content={
          <StyledAuthModalWrapper>
            {/* FORM */}
            <StyledAuthFormWrapper>
              {/* LOGO */}
              {isDesktop && !isAuthCloseTemporarilyDisabled && (
                <StyledLogoSpacer>
                  <ThemedFluerLogo />
                </StyledLogoSpacer>
              )}
              <Form form={form} style={styleMainForm} layout="vertical" requiredMark={false}>
                {/* FORM VIEW SWITCHER */}
                <AuthFlow
                  initView={initView}
                  onClose={() => (!isAuthCloseDisabledComputed ? externalOnClose?.() : null)}
                  onLogin={externalOnLogin}
                  labels={labels}
                  isDesygnerUser={isDesygnerUser}
                />
              </Form>
            </StyledAuthFormWrapper>

            {/* AUTH IMAGE */}
            {isDesktop && (
              <StyledImageWrapper>
                <Skeleton.Image style={getStyleAuthImage(theme)} active={isImageLoading} />
                <Image
                  src={authImgSrcComputed}
                  preview={false}
                  style={getStyleAuthImage(theme)}
                  height={PRESETS.authImageHeightPx}
                  width={PRESETS.authImageWidthPx}
                  alt="Illustrative image"
                  onLoad={() => {
                    setIsImageLoading(false);
                  }}
                />
              </StyledImageWrapper>
            )}
          </StyledAuthModalWrapper>
        }
      />
      {useGoogleTapOnSign && isGoogleTapOnSignVisible && (
        <StyledGoogleOneTapWrapper>
          <GoogleLogin
            onSuccess={(response) => {
              if (response.credential) {
                onHandleGoogleTapOnSignIn(response.credential);
              }
            }}
            useOneTap
          />
        </StyledGoogleOneTapWrapper>
      )}
    </>
  );
};
